import { ConfirmationDialog } from "@components/ConfirmationDialog";
import { Container } from "@components/crud/Container";
import { Footer } from "@components/crud/Footer";
import { Form } from "@components/crud/Form";
import { Toolbar } from "@components/crud/Toolbar";
import Grid from "@mui/material/Unstable_Grid2";
import { useAdminSportLocationPost } from "@sportsgravyengineering/sg-api-react-sdk";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { organizationAtom } from "../../recoil/auth";
import { useRecoilState } from "recoil";
import { ChangeEvent, useState } from "react";
import { AddressAutocomplete } from "@components/AddressAutocomplete";
import MapIcon from "../../assets/icons/mapIcon.svg";
import { Typography } from "@mui/material";
import GoogleMap from "@components/GoogleMap";
import { FormInput } from "@components/FormInput";
import { capitalizeFirstCharacter } from "@utils/capitalize";
export const SportLocationCreate = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [address, setAddress] = useState("");
  const [openAddressPicker, setOpenAddressPicker] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [organizationId] = useRecoilState(organizationAtom);
  const closeAddressChange = () => {
    setOpenAddressPicker(false);
  };
  const {
    handleSubmit,
    setFocus,
    control,
    formState: { isValid },
    reset,
    setValue,
    getValues,
    trigger
  } = useForm({
    mode: "onBlur"
  });
  const { mutate: save, isLoading: isSaving } = useAdminSportLocationPost();
  const saveHandler =
    (resetInsteadOfRoute = false) =>
    async (formValues) => {
      const values = {
        ...formValues.addressComponents,
        name: formValues.name,
        organizationId
      };
      save(
        {
          data: values
        },
        {
          onSuccess: () => {
            enqueueSnackbar("Sport Location added successfully!", {
              variant: "success"
            });
            if (resetInsteadOfRoute) {
              reset();
            } else {
              navigate("/sport-locations");
            }
          },
          onError: () => {
            enqueueSnackbar("Failed to add sport location!", {
              variant: "error"
            });
          }
        }
      );
    };
  const onAddressChanges = (changedAddress) => {
    const streetNumber = changedAddress.find((c) =>
      c.types.includes("street_number")
    );
    const route = changedAddress.find((c) => c.types.includes("route"));
    const address1 = `${streetNumber?.long_name || ""} ${
      route?.long_name || ""
    }`;
    const country = changedAddress.find((c) => c.types.includes("country"));
    const state = changedAddress.find((c) =>
      c.types.includes("administrative_area_level_1")
    );
    let city = changedAddress.find((c) => c.types.includes("locality"));
    if (!city)
      city = changedAddress.find(
        (c) =>
          c.types.includes("sublocality") || c.types.includes("postal_town")
      );
    const zip = changedAddress.find((c) => c.types.includes("postal_code"));

    setValue("addressComponents", {
      postalCode: zip?.short_name || zip?.long_name,
      locality: city?.short_name || city?.long_name,
      province: state.short_name,
      country: country.short_name,
      lines: [address1]
    });
    setValue("name", "");
    trigger("name");
  };
  const setPlace = (place) => {
    setValue("name", place, { shouldValidate: true });
    trigger("name");
    setSaveDisabled(false);
  };
  const formattedAddress = (address) => {
    setAddress(address);
    setValue("location", address);
    trigger("location");
    setFocus("location");
    setSaveDisabled(getValues().name === "");
  };
  const selectAddressSuggestion = (place) => {
    setValue("name", place?.name, { shouldValidate: true });
    trigger("name");
    const addressComponents = place?.address_components || [];
    const streetNumber = addressComponents.find((c) =>
      c.types.includes("street_number")
    );
    const route = addressComponents.find((c) => c.types.includes("route"));
    const address1 = `${streetNumber?.long_name || ""} ${
      route?.long_name || ""
    }`;
    const country = addressComponents.find((c) => c.types.includes("country"));
    const state = addressComponents.find((c) =>
      c.types.includes("administrative_area_level_1")
    );
    let city = addressComponents.find((c) => c.types.includes("locality"));
    if (!city)
      city = addressComponents.find(
        (c) =>
          c.types.includes("sublocality") || c.types.includes("postal_town")
      );
    const zip = addressComponents.find((c) => c.types.includes("postal_code"));
    setValue("addressComponents", {
      postalCode: zip?.short_name || zip?.long_name,
      locality: city?.short_name || city?.long_name,
      province: state.short_name,
      country: country.short_name,
      lines: [address1]
    });
    setValue("location", place.formatted_address);
    trigger("location");
    setFocus("name");
    setAddress(place.formatted_address);
    setSaveDisabled(false);
  };
  return (
    <Container>
      <Toolbar title="Add Sports Location" />
      <Form>
        <Grid data-testid="sportLocation-add-form" container spacing={3}>
          <Grid xs={12} md={12} container alignItems="center">
            <Grid xs={11} data-testid="sportLocation-location">
              <AddressAutocomplete
                name="location"
                control={control}
                selectSuggestion={selectAddressSuggestion}
                rules={{
                  required: "Location is required"
                }}
                label="Location"
                required
                onChange={(e) => {
                  setSaveDisabled(true);
                  setAddress(e);
                }}
                trigger={trigger}
              />
            </Grid>
            <Grid>
              <Typography>&nbsp;</Typography>
              <img
                data-testid="map-icon"
                src={MapIcon}
                alt="Map Icon"
                style={{ cursor: "pointer" }}
                onClick={() => setOpenAddressPicker(true)}
              />
            </Grid>
          </Grid>
          <Grid xs={12} md={12} alignItems="center" container direction="row">
            <Grid xs={6} data-testid="sportLocation-name">
              <FormInput
                control={control}
                name="name"
                rules={{
                  required: "Name is required"
                }}
                onChange={(e) => {
                  const value = (e as ChangeEvent<HTMLInputElement>)?.target
                    .value;
                  if (value !== "") setSaveDisabled(false);
                  setValue("name", capitalizeFirstCharacter(value));
                }}
                label="Name"
                type="text"
                required
              />
            </Grid>
          </Grid>
        </Grid>
      </Form>
      <Footer
        cancelBtnClick={() => setOpenCancelDialog(true)}
        saveBtnClick={handleSubmit(saveHandler(false))}
        isDisabled={!isValid || isSaving || saveDisabled}
        isLoading={isSaving}
      />
      <ConfirmationDialog
        title="Are you sure you want to cancel?"
        body="All of your current changes will be lost."
        open={openCancelDialog}
        close={() => setOpenCancelDialog(false)}
        onCancel={() => setOpenCancelDialog(false)}
        onConfirm={() => navigate("/sport-locations")}
        cancelBtnText="Cancel"
        confirmBtnText="Confirm"
      />
      {openAddressPicker && (
        <GoogleMap
          close={closeAddressChange}
          onAddressChange={onAddressChanges}
          formattedAddress={formattedAddress}
          address={address}
          setPlace={setPlace}
        />
      )}
    </Container>
  );
};
